import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { fetchData } from './dataFetching';
import { transformData } from './dataTransformation';
import { useLoadingTracker } from '../contexts/LoadingTrackerContext';

const holeColumns = {
  'Hole No': 'Hole_ID',
  'X': 'NAT_East',
  'Y': 'NAT_North',
  'Z': 'NAT_RL',
  'Azimuth': 'Collar_Azimuth',
  'Dip': 'Collar_Dip',
  'Depth': 'Max_Depth',
};

const assayColumns = {
  'HoleID': 'Hole_ID',
  'from': 'Depth_From',
  'to': 'Depth_To',
  'grade': 'Au_ppm',
};

const constants =  [520000,1180,8320000]

/** Line2/LineMaterial can use AA fringes (alpha); that lets hole traces show through thick assays. */
const lineOpaqueProps = {
  alphaToCoverage: false,
  transparent: false,
  opacity: 1,
  depthWrite: true,
  depthTest: true,
}

/** Assays after holes; slight polygon offset so coincident segments win the depth test. */
const assayLineExtraProps = {
  renderOrder: 1,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -2,
}

export default function HoleRender({ name, hole_url, assay_url, visible, onLoadComplete, onLoadFailed }) {
  //console.log(hole_url,assay_url)
  const [combinedData, setCombinedData] = useState({});
  const [filterData, setFilterData] = useState({});
  const [filteredHoleIds, setFilteredHoleIds] = useState([]);
  const [holeCount, setHoleCount] = useState(0);
  const [assayCount, setAssayCount] = useState(0);
  const { startLoading, stopLoading } = useLoadingTracker();
  const objectName = name || `Holes_${hole_url}`;
  const groupId = objectName;
  const printedHoleIdsRef = useRef(new Set());

  useEffect(() => {
    console.log('[HoleRender] mount', { objectName, hole_url, assay_url, visible });
  }, [objectName, hole_url, assay_url, visible]);

  useEffect(() => {
    let cancelled = false;
    
    // Start tracking loading
    startLoading(objectName);

    const loadData = async (attempt = 0) => {
      if (cancelled) return;

      try {
        const data = await fetchData(hole_url, assay_url, holeColumns, assayColumns);
        if (cancelled) return;
        
        const transformed = transformData(data, holeColumns, assayColumns, constants);
        if (!cancelled) {
          const holeN = Object.keys(transformed).length;
          let assayN = 0;
          Object.values(transformed).forEach((h) => {
            assayN += h.assays?.length || 0;
          });
          console.log(`[HoleRender] ${objectName}: parsed ${holeN} holes, ${assayN} assay intervals (collar/assay CSVs OK)`);
          if (holeN === 0) {
            console.warn('[HoleRender] 0 holes after transform — check collar CSV has Hole_ID, NAT_East, NAT_North, NAT_RL, Max_Depth, Collar_Azimuth, Collar_Dip');
          }
          setCombinedData(transformed);
          stopLoading(objectName);
          onLoadComplete?.(objectName);
          if (attempt > 0) {
            console.log(`[HoleRender] Successfully loaded ${hole_url}/${assay_url} after ${attempt + 1} attempt(s)`);
          }
        }
      } catch (error) {
        if (cancelled) return;

        // Check if it's a retryable error
        const errorMessage = error?.message || error?.toString() || String(error) || '';
        const errorString = errorMessage.toLowerCase();
        const errorName = (error?.name || '').toLowerCase();
        const errorType = (error?.type || '').toLowerCase();

        const isHttp2Error = 
          errorString.includes('http2') ||
          errorString.includes('err_http2_protocol_error') ||
          errorString.includes('protocol_error') ||
          errorName.includes('http2') ||
          errorType.includes('http2');

        const isNetworkError = 
          errorString.includes('failed to fetch') ||
          errorString.includes('networkerror') ||
          errorString.includes('network') ||
          errorString.includes('aborted') ||
          errorString.includes('timeout') ||
          errorString.includes('load failed') ||
          errorString.includes('could not load') ||
          errorString.includes('fetch') ||
          errorName.includes('network') ||
          errorName.includes('abort') ||
          errorType === 'networkerror' ||
          errorType === 'abort';

        const isRetryableError = isHttp2Error || isNetworkError;

        // Always retry network/HTTP2 errors indefinitely
        // For other errors, retry up to 100 times as a safety measure
        if (isRetryableError || attempt < 100) {
          const backoffDelay = Math.min(Math.pow(2, Math.min(attempt, 5)) * 1000, 30000);
          const errorInfo = errorMessage || errorName || 'Unknown error';
          console.warn(`[HoleRender] Attempt ${attempt + 1} failed for ${hole_url}/${assay_url}${isRetryableError ? ' (network error)' : ''}, retrying in ${backoffDelay/1000}s...`, errorInfo);
          
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return loadData(attempt + 1);
        }

        // Only log error if we've truly exhausted retries (shouldn't happen for network errors)
        console.error(`[HoleRender] Failed to load ${hole_url}/${assay_url} after ${attempt + 1} attempts:`, error);
        onLoadFailed?.(objectName);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      stopLoading(objectName);
    };
  }, [hole_url, assay_url, objectName, startLoading, stopLoading]);

  useEffect(() => {
    if (holeCount > 0 && !visible) {
      console.warn('[HoleRender] Collars loaded but layer "Holes" is off — enable it in layer control (folder icon).');
    }
  }, [holeCount, visible]);

  useEffect(() => {
    const filteredData = filteredHoleIds.length>0
      ? Object.keys(combinedData).reduce((acc, key) => {
          if (filteredHoleIds.includes(key)) {
            acc[key] = combinedData[key];
          }
          return acc;
        }, {})
      : combinedData;

    setHoleCount(Object.keys(filteredData).length);
    let assayNumber = 0
    Object.keys(filteredData).forEach((hole, j) => {
        const data = filteredData[hole].assays;
        assayNumber += data.length
    })
    setAssayCount(assayNumber)
    
    setFilterData(filteredData)

    // Debug: print final hole coordinates (local + reconstructed original)
    try {
      const ids = Object.keys(filteredData);
      if (ids.length === 0 && Object.keys(combinedData).length > 0) {
        console.warn('[HoleRender] filteredData is empty but combinedData has rows — check filteredHoleIds');
      }
      if (ids.length === 0) {
        return;
      }
      console.log(`[HoleRender] logging ${ids.length} hole positions (expand group below)`);
      console.groupCollapsed(`[HoleRender] Final holes (${ids.length}) for ${objectName}`);
      ids.forEach((holeId) => {
        if (printedHoleIdsRef.current.has(holeId)) return;
        const holeObj = filteredData[holeId];
        const start = holeObj?.points?.[0];
        const end = holeObj?.points?.[1];
        if (!start || !end) return;

        const localStart = { x: start.x, y: start.y, z: start.z };
        const localEnd = { x: end.x, y: end.y, z: end.z };

        const originalStart = {
          east: start.x + constants[0],
          rl: start.y + constants[1],
          north: constants[2] - start.z,
        };
        const originalEnd = {
          east: end.x + constants[0],
          rl: end.y + constants[1],
          north: constants[2] - end.z,
        };

        console.log(holeId, { localStart, localEnd, originalStart, originalEnd });
        printedHoleIdsRef.current.add(holeId);
      });
      console.groupEnd();
    } catch (e) {
      console.warn('[HoleRender] coordinate debug print failed', e);
    }

    //console.log(holeCount,assayCount)

  }, [combinedData, filteredHoleIds]);

  return (
    holeCount > 0 && visible &&
    <group visible={visible} name={groupId} userData={{ groupId }}>
      <HoleStartMarkers count={holeCount} holes={filterData} />
      <HoleTraceLines holes={filterData} />
      {assayCount > 0 && <AssayLines holes={filterData} />}
    </group>
      
  );
}

/** Same Line pattern as `HolesLoader.js`: one Line per hole, collar → toe. */
function HoleTraceLines({ holes }) {
  return (
    <>
      {Object.keys(holes).map((holeId) => {
        const row = holes[holeId];
        const start = row.points?.[0];
        const end = row.points?.[1];
        if (!start || !end) return null;
        const points = [
          [start.x, start.y, start.z],
          [end.x, end.y, end.z],
        ];
        return (
          <Line
            key={holeId}
            points={points}
            closed={false}
            curveType="centripetal"
            userData={{ displayData: row.data }}
            lineWidth={5}
            color="black"
            name={holeId}
            {...lineOpaqueProps}
          />
        );
      })}
    </>
  );
}

function HoleStartMarkers({ count, holes }) {
  const ref = useRef();

  useEffect(() => {
    if (!ref.current) return;
    const ids = Object.keys(holes);
    for (let i = 0; i < ids.length; i += 1) {
      const holeId = ids[i];
      const start = holes[holeId]?.points?.[0];
      if (!start) continue;
      const matrix = new THREE.Matrix4().makeTranslation(start.x, start.y, start.z);
      ref.current.setMatrixAt(i, matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  }, [holes]);

  const sphereGeo = new THREE.SphereGeometry(2, 10, 10);
  return (
    <instancedMesh
      ref={ref}
      frustumCulled={false}
      args={[null, null, count]}
      name="HoleStartMarkers"
    >
      <primitive object={sphereGeo} attach="geometry" />
      <meshBasicMaterial color="#ffffff" />
    </instancedMesh>
  );
}

/** Assay intervals as Line segments (from → to), same Line component as `HolesLoader.js`. */
function AssayLines({ holes }) {
  const lines = [];
  Object.keys(holes).forEach((holeId) => {
    holes[holeId].assays.forEach((assay, idx) => {
      const a = assay.fromPoint;
      const b = assay.toPoint;
      if (!a || !b) return;
      const points = [
        [a.x, a.y, a.z],
        [b.x, b.y, b.z],
      ];
      const row = assay.data;
      const instancesData = [
        {
          holeID: row[assayColumns.HoleID],
          from: `${row[assayColumns.from]} m`,
          to: `${row[assayColumns.to]} m`,
          grade: `${row[assayColumns.grade]} g/t`,
        },
      ];
      lines.push(
        <Line
          key={`${holeId}-${row?.SampleID ?? idx}`}
          points={points}
          closed={false}
          curveType="centripetal"
          lineWidth={6}
          color={assay.color}
          name="Assays"
          userData={{ instancesData }}
          {...lineOpaqueProps}
          {...assayLineExtraProps}
        />
      );
    });
  });
  return <>{lines}</>;
}